import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Typography,
    FormControl,
    Pagination,
    IconButton,
    Tooltip,
    Button,
    Card,
    CardContent,
    Box,
    Stack,
    Select,
    MenuItem,
    InputLabel
} from "@mui/material";
import {
    DataGrid,
    GridToolbarContainer
} from '@mui/x-data-grid';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import LinearProgress from '@mui/material/LinearProgress';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import Date from '../../../pages/General/Date'
import NorowsData from '../../General/NorowsData'
import PickupDrawer from '../../General/PickupDrawer'
import PickupItemsDrawer from '../../General/PickupItemsDrawer'
import { fetchPickupDetails, resetPickupNotifications } from './pickupSlice';
import Notification from '../../../pages/General/Notification'
import Printer from '../../../pages/General/Printer'

const PickupPage = () => {
    const dispatch = useDispatch();
    const pickup = useSelector(state => state.pickup && state.pickup.pickupData);
    const pickupCreate = useSelector(state => state.pickup && state.pickup.createPickup);
    const [meta, setMeta] = useState(pickup && pickup.meta);
    const [pickupDrawerVisible, setPickupDrawerVisible] = useState(false);
    const [pickupItemDrawerVisible, setPickupItemDrawerVisible] = useState(false);
    const [pickupItems, setPickupItems] = useState({});
    const [pickupReferenceId, setPickupReferenceId] = useState("");
    const [printUrl, setPrintUrl] = useState("")
    const [isPrinted, setIsPrinted] = useState(true)

    const openPickupDrawer = () => {
        setPickupDrawerVisible(true)
    }

    const pickupOnClose = (values) => {
        setPickupDrawerVisible(false);
    };

    const oepnPickupItemsDrawer = (pickupid) => {
        let output = pickup && pickup.data.filter(({ _id }) => [pickupid].includes(_id));
        let finaloutput = Object.assign({}, output[0]);
        setPickupItems(finaloutput)
        setPickupItemDrawerVisible(true)
        setPickupReferenceId(pickupid)
    }

    const pickupItemsOnClose = () => {
        setPickupItemDrawerVisible(false);
    };

    const columns = [
        {
            field: '_id',
            headerName: 'Pickup Reference',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            flex: 1.2,
            renderCell: (params) => {
                return (
                    <Typography 
                        variant="body2" 
                        onClick={e => { oepnPickupItemsDrawer(params.row._id) }}
                        sx={{ 
                            color: '#1976d2', 
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            fontSize: '13.5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: '100%',
                            '&:hover': { textDecoration: 'underline' }
                        }}
                    >
                        {params.row._id}
                    </Typography>
                );
            }
        },
        {
            field: 'total_quantity',
            headerName: 'Total Quantity',
            headerClassName: 'super-app-theme--header',
            flex: 1,
            renderCell: (params) => {
                return (
                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600, fontSize: '13.5px', display: 'inline-flex', alignItems: 'center', height: '100%' }}>
                        {params.row.total_quantity ? params.row.total_quantity : "-"}
                    </Typography>
                );
            }
        },
        {
            field: 'created_at',
            headerName: 'Created Date',
            headerClassName: 'super-app-theme--header',
            flex: 1,
            renderCell: (params) => {
                return (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', height: '100%', '& *': { fontWeight: 500, color: '#475569', fontSize: '13.5px' } }}>
                        <Date date={params.row.created_at} />
                    </Box>
                );
            }
        },
        {
            field: 'action',
            headerName: 'Pickup Label',
            minWidth: 200,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                const isActionAvailable = !!(params.row.carrier_response && params.row.carrier_response.label_url_direct);
                
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                        <Tooltip title={isActionAvailable ? "View" : "Label Unavailable"} placement="top">
                            <span>
                                <IconButton 
                                    size="small"
                                    onClick={() => window.open(params.row.carrier_response.label_url_direct, '_blank')}
                                    disabled={!isActionAvailable}
                                    sx={{
                                        color: isActionAvailable ? '#1976d2' : '#94A3B8',
                                        padding: '6px',
                                        borderRadius: '8px',
                                        border: '1px solid',
                                        borderColor: isActionAvailable ? '#E2E8F0' : '#F1F5F9',
                                        backgroundColor: '#FFFFFF',
                                        transition: 'all 0.15s ease-in-out',
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                            borderColor: '#CBD5E1',
                                        },
                                        '&.Mui-disabled': { backgroundColor: '#F8FAFC', borderColor: '#F1F5F9' }
                                    }}
                                >
                                    <RemoveRedEyeIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        
                        <Tooltip title={isActionAvailable ? "Print" : "Label Unavailable"} placement="top">
                            <span>
                                <IconButton 
                                    size="small"
                                    onClick={() => { setPrintUrl(params.row.carrier_response.label_url_direct); setIsPrinted(false) }}
                                    disabled={!isActionAvailable}
                                    sx={{
                                        color: isActionAvailable ? '#1976d2' : '#94A3B8',
                                        padding: '6px',
                                        borderRadius: '8px',
                                        border: '1px solid',
                                        borderColor: isActionAvailable ? '#E2E8F0' : '#F1F5F9',
                                        backgroundColor: '#FFFFFF',
                                        transition: 'all 0.15s ease-in-out',
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                            borderColor: '#CBD5E1',
                                        },
                                        '&.Mui-disabled': { backgroundColor: '#F8FAFC', borderColor: '#F1F5F9' }
                                    }}
                                >
                                    <LocalPrintshopIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            }
        }
    ];

    const onSortChange = (currentSorting) => {
        setMeta({
            ...meta,
            ...{
                sorting: JSON.stringify(currentSorting)
            }
        });
    }

    const onChangePerPage = (event) => {
        setMeta({
            ...meta,
            ...{
                page: 1,
                per_page: event.target.value
            }
        });
    }

    const onChangePagination = (event, page) => {
        setMeta({
            ...meta,
            ...{
                page: page
            }
        });
    }

    function CustomTotalRows() {
        return (
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748B', fontSize: '13px', display: 'inline-flex', alignItems: 'center' }}>
                Showing {pickup && pickup.data && pickup.data.length} of {(pickup && pickup.meta && pickup.meta.total) ? pickup.meta.total : 0} records
            </Typography>
        )
    }

    function CustomPagesizeoptions() {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748B', fontSize: '13px' }}>
                    Show
                </Typography>
                <select
                    value={meta && meta.per_page}
                    onChange={onChangePerPage}
                    style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: '1px solid #E2E8F0',
                        color: '#0F172A',
                        fontWeight: 600,
                        fontSize: '13px',
                        outline: 'none',
                        backgroundColor: '#FFFFFF',
                        cursor: 'pointer'
                    }}
                >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </select>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748B', fontSize: '13px' }}>
                    entries
                </Typography>
            </Box>
        )
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0', backgroundColor: '#F8FAFC' }}>
                <CustomPagesizeoptions />
                <CustomTotalRows />
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {
        return (
            <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'center', borderTop: '1px solid #E2E8F0', backgroundColor: '#FFFFFF' }}>
                <Pagination
                    color="primary"
                    shape="rounded"
                    size="small"
                    page={pickup && pickup.meta && pickup.meta.current_page}
                    count={pickup && pickup.meta && pickup.meta.last_page}
                    onChange={onChangePagination}
                    sx={{
                        '& .MuiPaginationItem-root': { fontWeight: 700, borderRadius: '8px' },
                        '& .Mui-selected': { boxShadow: '0 2px 4px rgba(25, 118, 210, 0.15)' }
                    }}
                />
            </Box>
        );
    }

    useEffect(() => {
        dispatch(fetchPickupDetails(meta))
    }, [meta]);
   
    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', p: { xs: 2, md: 4 }, bgcolor: '#FAFAFA', minHeight: '100vh' }}>
            {(pickupCreate && pickupCreate.notification && pickupCreate.notification.show) &&
                <Notification
                    is_open={pickupCreate.notification.show}
                    messageType={pickupCreate.notification.messageType}
                    messages={pickupCreate.notification.messageContent}
                    handleClose={() => dispatch(resetPickupNotifications())}
                />}
            <Printer isPrinted={isPrinted} setIsPrinted={setIsPrinted} print_url={printUrl} />
            
            {/* Control Dashboard Header Canvas */}
            <Card 
                sx={{ 
                    mb: 3, 
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03)',
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ p: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '20px', m: 0, letterSpacing: '-0.5px' }}>
                        Pickup
                    </Typography>
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, className: "features-list" }}>
                        <Box component="li">
                            <Button 
                                variant="contained" 
                                onClick={openPickupDrawer}
                                startIcon={<LocalMallOutlinedIcon sx={{ fontSize: '18px !important' }} />}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                    fontSize: '13.5px',
                                    height: '38px',
                                    px: 3,
                                    boxShadow: 'none',
                                    backgroundColor: '#1976d2',
                                    color: '#FFFFFF',
                                    '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                }}
                            >
                                Create Pickup Request
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Card>

            {/* Centralized Grid Indexing Surface Component */}
            <Card 
                sx={{ 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '16px', 
                    overflow: 'hidden', 
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03)',
                    backgroundColor: '#FFFFFF'
                }}
            >
                <DataGrid
                    getRowId={(row) => row._id}
                    rows={pickup?.data || []}
                    columns={columns}
                    autoHeight={true}
                    disableColumnFilter={true}
                    disableColumnMenu={true}
                    sortingMode="server"
                    onSortModelChange={(newSortModel) => onSortChange(newSortModel)}
                    sortModel={meta && meta.sorting && JSON.parse(meta.sorting)}
                    disableSelectionOnClick
                    loading={!!(pickup && pickup.loading)}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px', '&:focus, &:focus-within': { outline: 'none' } },
                        '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease-in-out', borderBottom: '1px solid #F1F5F9', alignItems: 'center', '&:hover': { backgroundColor: '#F8FAFC' } },
                        '& .MuiDataGrid-cell': { borderBottom: 'none', display: 'flex', alignItems: 'center', py: '12px', '&:focus, &:focus-within': { outline: 'none' } },
                        '& .MuiLinearProgress-root': { height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }
                    }}
                    slots={{
                        pagination: CustomPagination,
                        toolbar: CustomToolbar,
                        noRowsOverlay: NorowsData,
                        loadingOverlay: LinearProgress,
                    }}
                />
            </Card>
            
            <PickupDrawer
                visible={pickupDrawerVisible} pickupOnClose={pickupOnClose}
            />
            <PickupItemsDrawer
                pickupItems={pickupItems} visible={pickupItemDrawerVisible} pickupReferenceId={pickupReferenceId} pickupItemsOnClose={pickupItemsOnClose}
            />
        </Box>
    );
}

export default PickupPage;